import { Injectable } from "@angular/core";
import {
  Http,
  Headers,
  RequestOptions,
  Response,
  URLSearchParams,
} from "@angular/http";
import { AuthHttp } from "angular2-jwt";
import { Observable } from "rxjs/Observable";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import "rxjs/add/observable/throw";

import { Config } from "../config";
import { AuthenticationService } from "../services/authentication.service";
import { Department } from "../shared/models/department.model";
import { Comment } from "../shared/models/comment.model";
import { Interval } from "../shared/models/interval.model";
import { ReportData } from "../shared/models/reportData.model";
import { Agreement } from "../shared/models/agreement.model";
import { OccupancyColors } from "app/shared/models/occupancyColors.model";

/**
 * Dashboard service (to Report Web API controller).
 */
@Injectable()
export class DashboardService {
  headers: Headers;
  options: RequestOptions;

  constructor(
    private authHttp: AuthHttp,
    private http: Http,
    private authenticationService: AuthenticationService
  ) {
    // Creates header for post requests.
    this.headers = new Headers({ "Content-Type": "application/json" });
    this.options = new RequestOptions({ headers: this.headers });
  }

  /**
   * Get all active departments
   */
  public getDepartments(): Observable<Department[]> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    params.set("active", "true");
    params.set("overview", "true");
    let cb = new Date().getTime();
    params.set("cacheBuster", cb.toString());
    this.options.search = params;
    return this.authHttp
      .get(Config.BaseUrl + "/api/Report/GetDepartments", this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Get comments for departments or chosen departments
   * @param departments
   */
  public getComments(departments: Department[]): Observable<Comment[]> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    params.set("getLatest", "true");
    params.set("overview", "true");
    let cb = new Date().getTime();
    params.set("cacheBuster", cb.toString());
    this.options.search = params;
    let body: Department[] = departments;
    return this.authHttp
      .post(Config.BaseUrl + "/api/Report/GetComments", body, this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Get intervals
   */
  public getIntervals(): Observable<Interval[]> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    let cb = new Date().getTime();
    params.set("cacheBuster", cb.toString());
    this.options.search = params;
    return this.authHttp
      .get(Config.BaseUrl + "/api/Report/GetIntervals", this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Get report data
   * @param departments
   * @param intervals
   */
  public getReportData(
    departments: Department[],
    intervals: Interval[]
  ): Observable<ReportData[]> {
    let intervalIds: string = intervals.map((item) => item.id).join(",");
    //console.log(intervalIds);
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    params.set("intervalIds", intervalIds);
    let cb = new Date().getTime();
    params.set("cacheBuster", cb.toString());
    this.options.search = params;
    let body: Department[] = departments;
    return this.authHttp
      .post(Config.BaseUrl + "/api/Report/GetReportData", body, this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Get all Agreements
   */
  public getAgreement(): Observable<Agreement[]> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    //let cb = new Date().getTime();
    //params.set('cacheBuster', cb.toString());
    this.options.search = params;
    return this.authHttp
      .get(Config.BaseUrl + "/api/Report/GetAgreement", this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  public getAllOccupancyColors(): Observable<OccupancyColors[]> {
    let params: URLSearchParams = new URLSearchParams();
    params.set("userId", this.authenticationService.userSubject.value.sub);
    this.options.search = params;
    return this.authHttp
      .get(Config.BaseUrl + "/api/Report/GetAllOccupancyColors", this.options)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Create a new Agreement
   * @param Agreement
   */
  public createAgreement(agreement: Agreement): Observable<Agreement> {
    // let params: URLSearchParams = new URLSearchParams();
    // params.set('userId', this.authenticationService.userSubject.value.sub);
    // this.options.search = params;
    agreement.userId = this.authenticationService.userSubject.value.sub;
    let body: Agreement = agreement;

    return this.authHttp
      .post(Config.BaseUrl + "/api/Report/CreateAgreement", body)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Update single Agreement
   * @param Agreement
   */
  public updateAgreement(agreement: Agreement): Observable<Agreement> {
    // let params: URLSearchParams = new URLSearchParams();
    // params.set('adminId', this.authenticationService.userSubject.value.sub);
    // this.options.search = params;
    agreement.userId = this.authenticationService.userSubject.value.sub;
    let body: Agreement = agreement;
    return this.authHttp
      .post(Config.BaseUrl + "/api/Report/UpdateAgreement", body)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }

  /**
   * Delete single Agreement
   * @param Agreement
   */
  public deleteAgreement(agreement: Agreement): Observable<Agreement> {
    // let params: URLSearchParams = new URLSearchParams();
    // params.set('adminId', this.authenticationService.userSubject.value.sub);
    // this.options.search = params;
    agreement.userId = this.authenticationService.userSubject.value.sub;
    let body: Agreement = agreement;
    return this.authHttp
      .post(Config.BaseUrl + "/api/Report/DeleteAgreement", body)
      .map((res: Response) => {
        return res.json();
      })
      .catch((error: any) => {
        return Observable.throw(error);
      });
  }
}
